import React, { useContext, useEffect, useState } from 'react'
import { Button, ListMenu, ListMenuButton, Modal, NotificationInline, NotificationManagerContext,
   Popover, Spacer, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { Link, Route, Switch, useHistory, useRouteMatch } from 'react-router-dom'
import { LogoOutline24Icon, SocialMediaOutline24Icon } from 'assets/icons'
import NavLinkListItemRow from 'src/apps/AdminWorkspace/components/NavLinkListItemRow'
import ActionListItemRow from 'src/components/Settings/ActionListItemRow'
import ProtoHeadingBlock from 'src/components/Settings/ProtoHeadingBlock'
import ProtoReturnBar from 'src/components/Settings/ProtoReturnBar'
import { useOrganizationWithOptions, useUpdateOrganization } from 'src/graphql/hooks'
import ThemeContext from 'src/libraries/contexts/Theme'
import LogoEditor from '~/AdminWorkspace/apps/LogoEditor'
import SocialMediaEditor from '~/AdminWorkspace/apps/SocialMediaEditor'
import LinkCreate from './LinkCreate'
import LinkEdit from './LinkEdit'

const AdminWorkspaceMainMenu = ({ returnLinkName, returnLinkTo }) => {
   const history = useHistory()
   const match = useRouteMatch()

   const { organization, loading } = useOrganizationWithOptions({
      withGlobalNavLinksTargetMetadata: true
   })

   const { updateOrganization, mutating } = useUpdateOrganization({
      variables: {
         withGlobalNavLinksTargetMetadata: true
      }
   })

   const theme = useContext(ThemeContext)
   const { showNotification } = useContext(NotificationManagerContext)
   const [linkItems, setLinkItems] = useState([])
   const [modalIsOpen, setModalIsOpen] = useState(true)
   const [popoverConfig, setPopoverConfig] = useState({
      anchorElement: null,
      focusedValue: null,
      isOpen: false
   })

   const handleDragEnd = async (result) => {
      const newLinkItems = [...linkItems]
      const [removed] = newLinkItems.splice(result.source.index, 1)
      newLinkItems.splice(result.destination.index, 0, removed)
      const newLinkIds = newLinkItems.map(l => l.id)
      setLinkItems(newLinkItems)

      try {
         await updateOrganization({
            globalNavLinks: [{
               operation: 'reorder',
               reorder: newLinkIds
            }]
         })

         showNotification({
            message: 'Updated order for Main Menu links successfully',
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         showNotification({
            message: err?.graphQLErrors?.map(e => e.message).join(', ') || err.message,
            title: 'Error',
            type: 'error'
         })
      }
   }

   const handleRemoveLink = async (id) => {
      try {
         await updateOrganization({
            globalNavLinks: [{
               operation: 'remove',
               remove: id
            }]
         })

         showNotification({
            message: 'Main Menu link removed successfully',
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         showNotification({
            message: err?.graphQLErrors?.map(e => e.message).join(', ') || err.message,
            title: 'Error',
            type: 'error'
         })
      }
   }

   const handleClosePopover = () => {
      setPopoverConfig(config => ({
         ...config,
         isOpen: false,
         anchorElement: null
      }))
   }

   useEffect(() => {
      setLinkItems(organization?.globalNavLinks || [])
   }, [organization?.globalNavLinks])

   if (loading) {
      return null
   }

   return (
      <View>
         <ProtoReturnBar
            display="block[a-d] none[e-f]"
            returnLinkName={returnLinkName}
            returnLinkTo={returnLinkTo}
         />
         <View display="flex" justifyContent="center">
            <View
               maxWidth="100%[a-c] 600px[d-f]"
               padding="40px 20px">
               <View
                  width="100%"
                  display="flex"
                  flexFlow="column[a-c] row[d-f]">
                  <View flex="1 1 auto" paddingRight="30px[b-f]">
                     <ProtoHeadingBlock
                        title="Main Menu"
                        description="Customize the Main Menu that appears on your site. Add new links to pages, your content or external links. You can also organize the order that the pages appear in the navigation."
                     />
                  </View>
                  <View flex="0 0 auto" paddingTop="30px">
                     <Button
                        linkTo={`${match.url}/create`}
                        width="120px"
                        name="Add Link"
                        color={theme.protoSettingsHighlightBackgroundColor}
                        textColor={theme.protoSettingsHighlightForegroundColor}
                        textWeight="medium"
                        size="md"
                        textSize="0.9"
                        padding="24px"
                     />
                  </View>
               </View>
               <View width="100%" padding="12px 0px">
                  <Spacer size="5" />
                  <Text size="2" weight="medium" color="#000">
                     Main Menu Items
                  </Text>
               </View>
               {mutating && (
                  <View marginBottom="12px">
                     <NotificationInline
                        borderRadius="6px"
                        type="loading"
                        title="Updating..."
                        message="Update in progress"
                        paddingHorizontal="12px"
                        paddingVertical="12px"
                        textSize="1.5"
                     />
                  </View>
               )}
               <View
                  width="100%"
                  borderRadius="3px"
                  backgroundColor="#fff">
                  <NavLinkListItemRow
                     borderStyle="none"
                     name="Home"
                     entityType="Main Landing Page"
                     url="/"
                  />
                  <NavLinkListItemRow
                     name="Explore"
                     entityType="Custom Page"
                     url="/explore"
                  />
                  <View width="100%" backgroundColor="#f5f5f5">
                     <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="droppable">
                           {(provided, snapshot) => (
                              <div
                                 {...provided.droppableProps}
                                 ref={provided.innerRef}>
                                 {linkItems.map((item, index) => {
                                    let entityType = 'External URL'
                                    let entityName

                                    if (item.page) {
                                       entityType = 'Custom Page'
                                       entityName = item.page.title
                                    } else if (item.initiative) {
                                       entityType = item.initiative.type.nameSingular
                                       entityName = item.initiative.name
                                    }

                                    return (
                                       <Draggable
                                          key={item.id}
                                          draggableId={item.id}
                                          index={index}>
                                          {(elementProvided, elementSnapshot) => (
                                             <div
                                                ref={elementProvided.innerRef}
                                                {...elementProvided.draggableProps}
                                                style={{
                                                   ...elementProvided.draggableProps.style,
                                                   left: '0px !important'
                                                }}>
                                                <NavLinkListItemRow
                                                   archived={item?.page?.archived ||
                                                         item?.initiative?.archived}
                                                   draggable
                                                   dragHandleProps={elementProvided.dragHandleProps}
                                                   entityName={entityName}
                                                   entityType={entityType}
                                                   isDragging={elementSnapshot.isDragging}
                                                   name={item.name}
                                                   onOptionsButtonClick={(e) => {
                                                      setPopoverConfig({
                                                         isOpen: true,
                                                         anchorElement: e.currentTarget,
                                                         focusedValue: item
                                                      })
                                                   }}
                                                   unpublished={item.__typename === 'NavInternalLinkElement' &&
                                                      !item?.page?.published &&
                                                      !item?.initiative?.privacy === 'draft'}
                                                   url={item.url}
                                                />
                                             </div>
                                          )}
                                       </Draggable>
                                    )
                                 })}
                                 {provided.placeholder}
                              </div>
                           )}
                        </Droppable>
                     </DragDropContext>
                  </View>
                  <Popover
                     anchorElement={popoverConfig.anchorElement}
                     anchorOriginHorizontal="right"
                     anchorOriginVertical="top"
                     backgroundColor="#fff"
                     borderRadius="6px"
                     width="120px"
                     onBodyClick={handleClosePopover}
                     open={popoverConfig.isOpen}>
                     <View
                        float="left"
                        width="100%"
                        backgroundColor="#fff"
                        borderRadius="6px"
                        boxShadow="3px 3px 12px rgba(0,0,0,0.2)">
                        {popoverConfig.focusedValue && (
                           <ListMenu buttonSize="sm">
                              <ListMenuButton
                                 linkTo={`${match.url}/${popoverConfig.focusedValue.id}/edit`}
                                 name="Edit Link Name"
                              />
                              <ListMenuButton
                                 onClick={() => handleRemoveLink(popoverConfig.focusedValue.id)}
                                 name="Remove link"
                              />
                           </ListMenu>
                        )}
                     </View>
                  </Popover>
               </View>
               <Spacer size="6" />
               <ActionListItemRow
                  name="Logos"
                  description="Customize the logos used in your main menu, login page, email notifications and favicon."
                  action={(
                     <Link to={`${match.url}/logo-editor`}>
                        <Button
                           width="78px"
                           size="xs"
                           name="Edit"
                           color="#eee"
                           textColor="#222"
                        />
                     </Link>
                  )}
                  icon={(
                     <LogoOutline24Icon
                        strokeWidth="2px"
                        height="20px"
                        width="20px"
                        color="#aaa"
                     />
                  )}
               />
               <ActionListItemRow
                  name="Social Media"
                  description="Set your social media handles and links. These are displayed in the footer of your site."
                  action={(
                     <Link to={`${match.url}/social-media`}>
                        <Button
                           width="78px"
                           size="xs"
                           name="Edit"
                           color="#eee"
                           textColor="#222"
                        />
                     </Link>
                  )}
                  icon={(
                     <SocialMediaOutline24Icon
                        strokeWidth="2px"
                        height="20px"
                        width="20px"
                        color="#aaa"
                     />
                  )}
               />
            </View>
         </View>
         <Switch>
            <Route
               path={`${match.path}/create`}
               render={() => (
                  <Modal
                     borderRadius="6px"
                     width="330px"
                     onCloseTrigger={() => setModalIsOpen(false)}
                     onCloseComplete={() => {
                        history.push(match.url)
                        setModalIsOpen(true)
                     }}
                     open={modalIsOpen}>
                     <LinkCreate
                        onCloseButtonClick={() => setModalIsOpen(false)}
                        onCreate={() => {
                           setModalIsOpen(true)
                           history.push(match.url)
                        }}
                     />
                  </Modal>
               )}
            />
            <Route
               path={`${match.path}/:navLinkId/edit`}
               render={() => (
                  <Modal
                     borderRadius="6px"
                     width="270px"
                     onCloseTrigger={() => setModalIsOpen(false)}
                     onCloseComplete={() => {
                        history.push(match.url)
                        setModalIsOpen(true)
                     }}
                     open={modalIsOpen}>
                     <LinkEdit
                        onCloseButtonClick={() => setModalIsOpen(false)}
                        onUpdate={(page) => {
                           setModalIsOpen(true)
                           history.push(match.url)
                        }}
                     />
                  </Modal>
               )}
            />
            <Route
               path={`${match.url}/logo-editor`}
               render={() => (
                  <Modal
                     borderRadius="6px"
                     width="100%[a] 420px[b] 90%[c] 960px[d-f]"
                     height="100%[a] 80%[b] 95%[c] 80%[d-f]"
                     maxWidth="720px[c]"
                     maxHeight="600px[d-f]"
                     onCloseComplete={() => {
                        history.push(match.url)
                        setModalIsOpen(true)
                     }}
                     onCloseTrigger={() => setModalIsOpen(false)}
                     open={modalIsOpen}>
                     <LogoEditor
                        onCloseButtonClick={() => setModalIsOpen(false)}
                     />
                  </Modal>
               )}
            />
            <Route
               path={`${match.url}/social-media`}
               render={() => (
                  <Modal
                     borderRadius="6px"
                     width="100%[a] 360px[b] 600px[c-f]"
                     height="100%[a] 80%[b]"
                     onCloseComplete={() => {
                        history.push(match.url)
                        setModalIsOpen(true)
                     }}
                     onCloseTrigger={() => setModalIsOpen(false)}
                     open={modalIsOpen}>
                     <SocialMediaEditor
                        onCloseButtonClick={() => setModalIsOpen(false)}
                     />
                  </Modal>
               )}
            />
         </Switch>
      </View>
   )
}

AdminWorkspaceMainMenu.propTypes = {
   returnLinkName: PropTypes.string.isRequired,
   returnLinkTo: PropTypes.string.isRequired
}

export default AdminWorkspaceMainMenu
